import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Euro, Trophy, TrendingUp, Users, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const STAGE_COLORS = {
  "Nieuw": "border-slate-300 bg-slate-50 text-slate-700",
  "Contact gelegd": "border-blue-300 bg-blue-50 text-blue-700",
  "Gekwalificeerd": "border-indigo-300 bg-indigo-50 text-indigo-700",
  "Voorstel": "border-amber-300 bg-amber-50 text-amber-700",
  "Follow-up": "border-cyan-300 bg-cyan-50 text-cyan-700",
  "Onderhandeling": "border-violet-300 bg-violet-50 text-violet-700",
};

// Solid hex per stage for the Salesforce-style chevron progress bar.
export const STAGE_HEX = {
  "Nieuw": "#64748b",
  "Contact gelegd": "#3b82f6",
  "Gekwalificeerd": "#6366f1",
  "Voorstel": "#f59e0b",
  "Follow-up": "#06b6d4",
  "Onderhandeling": "#8b5cf6",
};

const chevronClip = (first, last) => {
  if (first) return "polygon(0 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 0 100%)";
  if (last) return "polygon(0 0, 100% 0, 100% 100%, 0 100%, 13px 50%)";
  return "polygon(0 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 0 100%, 13px 50%)";
};

export const DealStageBar = ({ stages, current, onChange, disabled }) => {
  const idx = stages.indexOf(current);
  const [pop, setPop] = useState(-1);
  useEffect(() => { setPop(idx); const t = setTimeout(() => setPop(-1), 450); return () => clearTimeout(t); }, [idx]);
  return (
    <div className="flex w-full select-none" data-testid="deal-stage-bar">
      {stages.map((s, i) => {
        const done = i < idx, active = i === idx;
        const color = STAGE_HEX[s] || "#64748b";
        return (
          <button
            key={s} type="button" disabled={disabled} onClick={() => !disabled && i !== idx && onChange(s)}
            data-testid={`stage-step-${i}`} title={s}
            className={`relative flex-1 min-w-0 px-3 py-3 text-[11px] sm:text-xs font-bold tracking-tight transition-all duration-300 ease-out ${disabled ? "cursor-default" : "cursor-pointer hover:brightness-95"} ${active ? "z-10" : ""} ${pop === i ? "scale-[1.06]" : "scale-100"}`}
            style={{
              backgroundColor: active ? color : done ? `${color}26` : "#eef2f6",
              color: active ? "#fff" : done ? color : "#94a3b8",
              clipPath: chevronClip(i === 0, i === stages.length - 1),
              marginLeft: i === 0 ? 0 : -13,
              boxShadow: active ? "0 4px 14px -2px rgba(0,0,0,0.25)" : "none",
            }}
          >
            <span className="block truncate pl-1">{s}</span>
          </button>
        );
      })}
    </div>
  );
};

export const fmtEUR = (v) => v == null ? "—" : `€${Number(v).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}`;

const TypeBadge = ({ type, converted }) => {
  const isCustomer = type === "customer" || converted;
  return (
    <span className={`text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${isCustomer ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{isCustomer ? "Klant" : "Lead"}</span>
  );
};

const stageLabel = (d) => d.status === "won" ? "Gewonnen" : d.status === "lost" ? "Verloren" : d.status === "unqualified" ? "Unqualified" : d.stage;
const stageBadgeClass = (d) => d.status === "won" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
  : d.status === "lost" ? "border-red-300 bg-red-50 text-red-700"
  : d.status === "unqualified" ? "border-slate-300 bg-slate-100 text-slate-600"
  : (STAGE_COLORS[d.stage] || "border-slate-300 text-slate-600");

export const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: "Open pipeline", value: fmtEUR(stats.open_value), sub: `${stats.open_count} deals`, icon: Euro, color: "text-blue-700" },
    { label: "Gewonnen", value: fmtEUR(stats.won_value), sub: `${stats.won} deals`, icon: Trophy, color: "text-emerald-600" },
    { label: "Win-rate", value: `${stats.win_rate}%`, sub: `${stats.won}W · ${stats.lost}V`, icon: TrendingUp, color: "text-violet-600" },
    { label: "Open deals", value: stats.open_count, sub: "in pipeline", icon: Users, color: "text-slate-700" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="deal-stats">
      {cards.map((c) => (
        <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-500">{c.label}</p><c.icon className={`h-4 w-4 ${c.color}`} /></div>
          <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
          <p className="text-xs text-slate-400">{c.sub}</p>
        </div>
      ))}
    </div>
  );
};

const DealCard = ({ d, onOpen, onDragStart }) => (
  <div data-testid={`deal-card-${d.id}`} draggable onDragStart={() => onDragStart(d.id)} onClick={() => onOpen(d.id)}
    className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer">
    <div className="flex items-center justify-between gap-1.5">
      <p className="font-semibold text-slate-800 text-sm truncate">{d.contact_name || "—"}{d.possible_duplicate_of && <AlertTriangle title="Mogelijk duplicaat" className="inline h-3.5 w-3.5 text-amber-500 ml-1.5 -mt-0.5" />}</p>
      <TypeBadge type={d.contact_type} />
    </div>
    <div className="mt-2 flex items-center justify-between">
      <span className="text-sm font-bold text-blue-700">{fmtEUR(d.value)}</span>
      {d.product && <span className="text-[10px] text-slate-500 truncate max-w-[55%]" title={d.product}>{d.product}</span>}
    </div>
  </div>
);

export const DealBoard = ({ stages, deals, onOpen, onMove }) => {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  return (
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="deal-board">
      {stages.map((stage) => {
        const items = deals.filter((d) => d.stage === stage);
        const total = items.reduce((s, d) => s + Number(d.value || 0), 0);
        return (
          <div key={stage} onDragOver={(e) => { e.preventDefault(); setOver(stage); }}
            onDrop={() => { if (dragId) onMove(dragId, stage); setDragId(null); setOver(null); }}
            className={`flex-shrink-0 w-64 rounded-xl border p-2 ${over === stage ? "border-blue-400 bg-blue-50/40" : "border-slate-200 bg-slate-50/60"}`} data-testid={`deal-col-${stage}`}>
            <div className={`flex items-center justify-between rounded-md border px-2 py-1 mb-2 text-xs font-semibold ${STAGE_COLORS[stage] || "border-slate-300 bg-white text-slate-700"}`}>
              <span>{stage}</span><span>{items.length} · {fmtEUR(total)}</span>
            </div>
            <div className="space-y-2 min-h-[60px]">{items.map((d) => <DealCard key={d.id} d={d} onOpen={onOpen} onDragStart={setDragId} />)}</div>
          </div>
        );
      })}
    </div>
  );
};

export const DealTable = ({ deals, onOpen }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="deal-table">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
        <th className="py-2.5 px-4">Contact</th><th className="py-2.5 px-3">Fase</th><th className="py-2.5 px-3">Waarde</th>
        <th className="py-2.5 px-3">Product</th><th className="py-2.5 px-3">Sluit</th><th className="py-2.5 px-3">Agent</th>
      </tr></thead>
      <tbody>
        {deals.length === 0 ? <tr><td colSpan={6} className="text-center text-slate-400 py-8">Geen deals</td></tr> :
          deals.map((d) => (
            <tr key={d.id} data-testid={`deal-row-${d.id}`} onClick={() => onOpen(d.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
              <td className="py-2.5 px-4 font-medium text-slate-800"><TypeBadge type={d.contact_type} converted={d.converted_customer_id} /> <span className="ml-2">{d.contact_name}</span></td>
              <td className="py-2.5 px-3"><span className={`text-xs rounded-full border px-2 py-0.5 ${stageBadgeClass(d)}`}>{stageLabel(d)}</span></td>
              <td className="py-2.5 px-3 font-semibold text-blue-700">{fmtEUR(d.value)}</td>
              <td className="py-2.5 px-3 text-slate-500 truncate max-w-[12rem]">{d.product || "—"}</td>
              <td className="py-2.5 px-3 text-slate-400 text-xs">{d.expected_close_date || "—"}</td>
              <td className="py-2.5 px-3 text-slate-400 text-xs">{(d.owner_email || "—").split("@")[0]}</td>
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

/**
 * AddDealDialog. fixedContact = { type:'lead'|'customer', id, name } skips the picker.
 */
export const AddDealDialog = ({ meta, onCreated, fixedContact, triggerLabel = "Nieuwe deal", triggerClass = "bg-blue-700 hover:bg-blue-800" }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(fixedContact?.type || "lead");
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(fixedContact || null);
  const [newLead, setNewLead] = useState(false);
  const [nl, setNl] = useState({ name: "", email: "", phone: "", company: "" });
  const [f, setF] = useState({ product: "", value: "", stage: "Nieuw" });
  const [existing, setExisting] = useState([]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  // Waarschuw als het gekozen contact al een open/gewonnen deal heeft.
  useEffect(() => {
    if (!open) { setExisting([]); return; }
    const params = {};
    if (fixedContact) params[fixedContact.type === "customer" ? "customer_id" : "lead_id"] = fixedContact.id;
    else if (picked) params[mode === "customer" ? "customer_id" : "lead_id"] = picked.id;
    else if (newLead && nl.email.trim()) params.email = nl.email.trim();
    if (!Object.keys(params).length) { setExisting([]); return; }
    const t = setTimeout(() => {
      api.get("/deals/check-duplicate", { params }).then((r) => setExisting(r.data.deals || [])).catch(() => setExisting([]));
    }, 300);
    return () => clearTimeout(t);
  }, [open, picked, fixedContact, newLead, nl.email, mode]);

  const openDialog = (o) => { setOpen(o); if (!o) { setQuery(""); setMatches([]); } };

  // Server-side search (debounced) for the lead/customer picker.
  useEffect(() => {
    if (fixedContact || picked) return;
    const term = query.trim();
    if (!term) { setMatches([]); return; }
    const url = mode === "customer" ? `/customers/search?q=${encodeURIComponent(term)}` : `/leads?q=${encodeURIComponent(term)}`;
    const t = setTimeout(() => {
      api.get(url).then((r) => setMatches((r.data || []).slice(0, 8))).catch(() => setMatches([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, mode, fixedContact, picked]);

  const submit = async () => {
    setBusy(true);
    try {
      let body = { product: f.product || null, value: f.value ? Number(f.value) : null, stage: f.stage };
      if (fixedContact) {
        body.contact_type = fixedContact.type;
        body[fixedContact.type === "customer" ? "customer_id" : "lead_id"] = fixedContact.id;
      } else if (mode === "customer") {
        if (!picked) { toast.error("Kies een klant"); setBusy(false); return; }
        body.contact_type = "customer"; body.customer_id = picked.id;
      } else if (newLead) {
        if (!nl.name.trim()) { toast.error("Naam is verplicht"); setBusy(false); return; }
        const { data: lead } = await api.post("/leads", nl);
        body.contact_type = "lead"; body.lead_id = lead.id;
      } else {
        if (!picked) { toast.error("Kies een lead"); setBusy(false); return; }
        body.contact_type = "lead"; body.lead_id = picked.id;
      }
      const { data } = await api.post("/deals", body);
      toast.success("Deal toegevoegd");
      setOpen(false); setPicked(fixedContact || null); setNewLead(false);
      setNl({ name: "", email: "", phone: "", company: "" }); setF({ product: "", value: "", stage: "Nieuw" });
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aanmaken mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={openDialog}>
      <DialogTrigger asChild>
        <Button data-testid="add-deal-btn" size={fixedContact ? "sm" : "default"} className={`${triggerClass} gap-2`}><Plus className="h-4 w-4" /> {triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nieuwe deal</DialogTitle>
          <DialogDescription>{fixedContact ? `Voor ${fixedContact.name}` : "Koppel een nieuwe deal aan een lead of bestaande klant."}</DialogDescription>
        </DialogHeader>

        {!fixedContact && (
          <>
            <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50 text-sm font-medium">
              <button data-testid="mode-lead" onClick={() => { setMode("lead"); setPicked(null); }} className={`flex-1 rounded-md py-1.5 ${mode === "lead" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}>Aan een lead</button>
              <button data-testid="mode-customer" onClick={() => { setMode("customer"); setPicked(null); setNewLead(false); }} className={`flex-1 rounded-md py-1.5 ${mode === "customer" ? "bg-white shadow-sm text-emerald-700" : "text-slate-500"}`}>Aan een klant</button>
            </div>

            {!(mode === "lead" && newLead) && (
              <div className="space-y-1 pt-1">
                <Label>{mode === "customer" ? "Klant" : "Lead"} zoeken</Label>
                {picked ? (
                  <div className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm">
                    <span className="font-medium text-blue-800">{picked.name} <span className="font-normal text-blue-600">{picked.email}</span></span>
                    <button onClick={() => setPicked(null)} className="text-blue-700 text-xs underline">wijzig</button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input data-testid="contact-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoek op naam of e-mail…" />
                    {matches.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-52 overflow-y-auto">
                        {matches.map((c) => (
                          <button key={c.id} data-testid={`contact-option-${c.id}`} onClick={() => { setPicked(c); setQuery(""); }} className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                            <span className="font-medium text-slate-800">{c.name}</span> <span className="text-slate-400">{c.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {mode === "lead" && <button data-testid="new-lead-toggle" onClick={() => setNewLead(true)} className="mt-1.5 text-xs text-blue-700 underline">+ Nieuwe lead aanmaken</button>}
                  </div>
                )}
              </div>
            )}

            {mode === "lead" && newLead && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="col-span-2 flex items-center justify-between"><Label>Nieuwe lead</Label><button onClick={() => setNewLead(false)} className="text-xs text-slate-400 underline">bestaande kiezen</button></div>
                <Input data-testid="nl-name" placeholder="Naam *" value={nl.name} onChange={(e) => setNl((p) => ({ ...p, name: e.target.value }))} />
                <Input data-testid="nl-email" placeholder="E-mail" value={nl.email} onChange={(e) => setNl((p) => ({ ...p, email: e.target.value }))} />
                <Input placeholder="Telefoon" value={nl.phone} onChange={(e) => setNl((p) => ({ ...p, phone: e.target.value }))} />
                <Input placeholder="Bedrijf" value={nl.company} onChange={(e) => setNl((p) => ({ ...p, company: e.target.value }))} />
              </div>
            )}
          </>
        )}

        {existing.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="dup-warning">
            <p className="font-semibold flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Dit contact heeft al {existing.length === 1 ? "een deal" : `${existing.length} deals`}:
            </p>
            <div className="space-y-1">
              {existing.map((d) => (
                <p key={d.id} className="flex items-center justify-between gap-2">
                  <span>{d.product || "Geen product"} · {d.status === "won" ? "Gewonnen" : "Open"}{d.value ? ` · €${Number(d.value).toLocaleString("nl-NL")}` : ""}</span>
                  <Link to={`/sales/deals/${d.id}`} className="text-blue-700 hover:underline shrink-0" data-testid={`dup-warning-open-${d.id}`}>Openen</Link>
                </p>
              ))}
            </div>
            <p className="mt-1.5 text-amber-700">Weet je zeker dat je nóg een deal wilt aanmaken?</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2 space-y-1">
            <Label>Product</Label>
            <Select value={f.product} onValueChange={(v) => setF((p) => {
              const prevDefault = meta.product_values?.[p.product];
              const newDefault = meta.product_values?.[v] ?? "";
              const valueWasAuto = !p.value || String(p.value) === String(prevDefault ?? "");
              return { ...p, product: v, value: valueWasAuto ? String(newDefault) : p.value };
            })}>
              <SelectTrigger data-testid="deal-product-select"><SelectValue placeholder="Kies product…" /></SelectTrigger>
              <SelectContent>{(meta.products || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>Waarde (€)</Label><Input data-testid="deal-value-input" type="number" value={f.value} onChange={set("value")} /></div>
          <div className="space-y-1">
            <Label>Fase</Label>
            <Select value={f.stage} onValueChange={set("stage")}>
              <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="deal-submit-btn" onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800 gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Toevoegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
